/**
 *	Source-file <-> URL mapping for documentation pages.
 *
 *	Pure functions, no filesystem access — the generator (@shared/bun/docs) and
 *	each app's runtime resolver both build on these, so the rules live in
 *	exactly one place. Browser-safe, which is why this sits here rather than
 *	under bun/.
 *
 *	Every function takes the collection's URL root, because an app can serve
 *	more than one collection and each has its own — see @shared/docs/types. The
 *	source paths below are relative to the collection's own directory; the
 *	generator prefixes them with the collection name afterwards.
 *
 *	    root /docs, index.md                  -> /docs
 *	    root /docs, README.md                 -> /docs
 *	    root /docs, getting_started/index.md  -> /docs/getting-started
 *	    root /build, atoms/button/README.md   -> /build/atoms/button
 *	    root /docs, foo/bar/baz/index.md      -> /docs/foo/bar/baz
 *	    root /docs, api/auth.md               -> /docs/api/auth
 *	    root /docs, contacts/index@v2.md      -> /docs/v2/contacts
 *	    root /docs, api/auth@v2.md            -> /docs/v2/api/auth
 *
 *	Underscores in a filename or directory become hyphens in the URL — the file
 *	on disk keeps its own name, and `source` still points at it.
 *
 *	A version modifier on the filename is *hoisted* to the front of the path so
 *	the URL reads `/docs/v2/contacts` rather than `/docs/contacts/v2`. That
 *	matches how the versioned API surface is addressed, and keeps a whole
 *	version of the docs under one prefix.
 */
import type { DocumentVersion } from '@shared/docs/types';

/** `index@v2` -> name `index`, version `v2`. */
const VERSION_PATTERN = /^(?<name>.+?)@(?<version>v\d+)$/;

/** Filenames that map to their directory rather than to a child segment. */
const DIRECTORY_BASENAMES = ['index', 'readme'];

/** `getting_started` -> `getting-started`. */
function toSegment(part: string): string {
	return part.replace(/_/g, '-');
}

export interface ParsedSource {
	source: string;
	segments: string[];
	version: Nullable<DocumentVersion>;
	url: string;
}

/** Joins canonical segments onto a collection's root. Empty segments give the root. */
export function toUrl(root: string, segments: string[]): string {
	return [root, ...segments].join('/');
}

/**
 *	Normalizes a router catch-all param into a canonical URL.
 *
 *	`uri.parse` coerces numeric-looking segments, so a page at `/docs/2024`
 *	arrives as a number — hence the `String()`. An absent param means the
 *	collection's root itself.
 */
export function toDocumentUrl(
	root: string,
	pagePath?: boolean | string | number
): string {
	if (pagePath === undefined || pagePath === null || pagePath === '') {
		return root;
	}

	const path = String(pagePath).replace(/^\/+|\/+$/g, '');

	if (!path) {
		return root;
	}

	return `${root}/${path}`;
}

/** Numeric weight of a version modifier, for picking the highest. `v2` -> 2. */
export function versionWeight(version: Nullable<DocumentVersion>): number {
	if (!version) {
		return 0;
	}

	return Number(version.slice(1));
}

/**
 *	Maps a source path relative to a collection's directory onto its canonical
 *	URL under that collection's root.
 *
 *	`segments` excludes the version, so two files that differ only by version
 *	share them — that is what groups a page into a version set.
 */
export function parseSource(root: string, source: string): ParsedSource {
	const normalized = source.replace(/\\/g, '/').replace(/\.md$/i, '');

	const parts = normalized.split('/').filter(Boolean);
	const basename = parts.pop() ?? '';

	const matched = basename.match(VERSION_PATTERN);

	const name = matched?.groups?.['name'] ?? basename;
	const version = (matched?.groups?.['version'] ??
		null) as Nullable<DocumentVersion>;

	const segments = parts.map(toSegment);

	if (!DIRECTORY_BASENAMES.includes(name.toLowerCase())) {
		segments.push(toSegment(name));
	}

	return {
		source,
		segments,
		version,
		url: toUrl(root, version ? [version, ...segments] : segments),
	};
}

/**
 *	A leading YAML frontmatter block, closed by its own `---` line. Matched in
 *	one place so the generator, the renderer and the copy-prompt all agree on
 *	where a page's metadata ends and its prose begins.
 */
const FRONTMATTER_PATTERN = /^---\r?\n([\s\S]*?)\r?\n---\r?\n/;

/** The block's raw YAML, or an empty string for a page that carries none. */
export function frontmatterBlock(source: string): string {
	return source.match(FRONTMATTER_PATTERN)?.[1] ?? '';
}

/** The page's prose, with any frontmatter block taken off the front. */
export function withoutFrontmatter(source: string): string {
	return source.replace(FRONTMATTER_PATTERN, '');
}

/**
 *	Best-effort page title: the first ATX heading in the source, ignoring any
 *	YAML frontmatter block. Falls back to a humanized last segment so a page
 *	without a heading still gets something usable in nav.
 *
 *	A page whose frontmatter names a `title` never reaches this — see the
 *	generator, which prefers the authored one. This is what answers for every
 *	page that carries no frontmatter at all.
 */
export function parseTitle(source: string, segments: string[]): string {
	const heading = withoutFrontmatter(source).match(/^#\s+(.+?)\s*$/m);

	if (heading?.[1]) {
		return heading[1];
	}

	const last = segments.at(-1);

	if (!last) {
		return 'Documentation';
	}

	return last
		.replace(/[-_]+/g, ' ')
		.replace(/(^|\s)([a-z])/g, (_, lead, char) => lead + char.toUpperCase());
}
